import { FC, useContext, useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import AppContext from 'src/store/appContext';
import AxiosContext from 'src/store/axiosContext';
import { INearbyMapResponse } from 'src/interfaces/interfaces';
import GoogleMap from '../Map/GoogleMap';

const pragueCenter = {
    lat: 50.08712109608858,
    lng: 14.417847885347497,
};

type AggregationMapProps = {
    standalone?: boolean;
};

const parseGps = (gps: string) => {
    const [lat, lng] = gps.split(',').map((value) => Number(value.trim()));

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
    }

    return { lat, lng };
};

const AggregationMap: FC<AggregationMapProps> = ({ standalone }) => {
    const { detail } = useContext(AppContext);
    const httpClient = useContext(AxiosContext);
    const defaultRadius = standalone ? 2000 : 800;
    const [regNum, setRegNum] = useState('');
    const [gps, setGps] = useState('');
    const [days, setDays] = useState('');
    const [radius, setRadius] = useState(defaultRadius);
    const [appliedParams, setAppliedParams] = useState<{
        days?: number;
        lat?: number;
        lng?: number;
        radius: number;
        regNum?: string;
        surveyId?: string;
    }>(
        standalone
            ? { lat: pragueCenter.lat, lng: pragueCenter.lng, radius: defaultRadius }
            : { radius: defaultRadius, surveyId: detail?.id }
    );
    const [nearbyMap, setNearbyMap] = useState<INearbyMapResponse>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!standalone && !detail?.id) {
            return;
        }

        const loadNearbyMap = async () => {
            setIsLoading(true);
            setError(null);
            const requestParams = standalone
                ? appliedParams
                : { radius: appliedParams.radius, surveyId: detail?.id };

            try {
                const response = await httpClient.get<INearbyMapResponse>(
                    `/surveys/nearby-map`,
                    {
                        params: requestParams,
                    }
                );

                setNearbyMap(response.data);
            } catch (e: any) {
                setError(e.response?.data?.message || 'Nepodařilo se načíst okolní podněty.');
            } finally {
                setIsLoading(false);
            }
        };

        loadNearbyMap();
    }, [appliedParams, detail?.id, httpClient, standalone]);

    const submitFilterHandler: React.FormEventHandler = (event) => {
        event.preventDefault();
        const nextParams: typeof appliedParams = { radius };

        if (standalone) {
            const trimmedRegNum = regNum.trim();
            const parsedGps = parseGps(gps);

            if (trimmedRegNum && gps.trim()) {
                setError('Vyplňte buď číslo podnětu, nebo GPS, ne oboje.');
                return;
            }

            if (trimmedRegNum) {
                nextParams.regNum = trimmedRegNum;
            }

            if (gps.trim()) {
                if (!parsedGps) {
                    setError('GPS zadejte ve formátu "50.0871, 14.4178".');
                    return;
                }

                nextParams.lat = parsedGps.lat;
                nextParams.lng = parsedGps.lng;
            }

            if (!trimmedRegNum && !gps.trim()) {
                nextParams.lat = pragueCenter.lat;
                nextParams.lng = pragueCenter.lng;
            }

            if (days.trim()) {
                nextParams.days = Number(days);
            }
        } else {
            nextParams.surveyId = detail?.id;
        }

        setAppliedParams(nextParams);
    };

    return (
        <div>
            <Form className="mb-2" onSubmit={submitFilterHandler}>
                <div className="d-flex flex-column flex-md-row gap-2 align-items-md-end">
                    {standalone && (
                        <>
                            <Form.Group controlId="nearby-map-reg-num">
                                <Form.Label>Číslo podnětu</Form.Label>
                                <Form.Control
                                    onChange={(e) => setRegNum(e.target.value)}
                                    placeholder="ZMT-2026-000123"
                                    type="text"
                                    value={regNum}
                                />
                            </Form.Group>
                            <Form.Group controlId="nearby-map-gps">
                                <Form.Label>GPS</Form.Label>
                                <Form.Control
                                    onChange={(e) => setGps(e.target.value)}
                                    placeholder="50.0871, 14.4178"
                                    type="text"
                                    value={gps}
                                />
                            </Form.Group>
                            <Form.Group controlId="nearby-map-days">
                                <Form.Label>Historie ve dnech</Form.Label>
                                <Form.Control
                                    min={1}
                                    onChange={(e) => setDays(e.target.value)}
                                    type="number"
                                    value={days}
                                />
                            </Form.Group>
                        </>
                    )}
                    <Form.Group controlId="nearby-map-radius">
                        <Form.Label>Radius v metrech</Form.Label>
                        <Form.Control
                            min={1}
                            onChange={(e) => setRadius(Number(e.target.value))}
                            type="number"
                            value={radius}
                        />
                    </Form.Group>
                    <Button type="submit" variant="outline-dark">
                        Filtrovat
                    </Button>
                    {nearbyMap && (
                        <span className="small text-muted mb-md-2">
                            Zobrazeno podnětů: {nearbyMap.surveys.length}
                        </span>
                    )}
                </div>
            </Form>
            {isLoading && (
                <div className="d-flex justify-content-center my-3">
                    <Spinner animation="border" />
                </div>
            )}
            {error && <Alert variant="danger">{error}</Alert>}
            {!isLoading && !error && (
                <GoogleMap
                    centerPosition={nearbyMap?.center || (standalone ? pragueCenter : undefined)}
                    mapHeight={standalone ? '70vh' : undefined}
                    nearests={nearbyMap?.surveys || []}
                    radius={nearbyMap?.radius || appliedParams.radius}
                />
            )}
        </div>
    );
};

export default AggregationMap;
